import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import AuthService from '../services/AuthService';

const EmailVerificationPage = ({ colors }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // 'checking' (read-only token validity check) -> 'pending' (valid, waiting for
  // the user to explicitly confirm) -> 'confirming' (consuming the token) -> 'success'.
  // Also: 'error', 'no-token'.
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  // Avoid a duplicate check call under React 18 StrictMode's dev-mode double-invoke
  // (mount -> unmount -> remount). Harmless either way since checking is read-only,
  // but no reason to double up the network call.
  const checkAttempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage('No verification token provided.');
      return;
    }

    if (checkAttempted.current) {
      return;
    }
    checkAttempted.current = true;

    const checkToken = async () => {
      try {
        const response = await AuthService.checkVerifyEmailToken(token);
        if (response.valid) {
          setStatus('pending');
        } else {
          setStatus('error');
          setMessage('This verification link is invalid or has expired.');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.message || 'Failed to check verification link.');
      }
    };

    checkToken();
  }, [token]);

  const handleConfirm = async () => {
    setStatus('confirming');
    try {
      const response = await AuthService.confirmVerifyEmail(token);
      setStatus('success');
      setMessage(response.message || 'Your email has been verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Failed to verify email. The link may be expired or invalid.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <CircularProgress size={64} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Checking Your Link
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary }}
            >
              Please wait while we check your verification link...
            </Typography>
          </>
        );

      case 'pending':
        return (
          <>
            <EmailIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Confirm Your Email
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              Click below to verify your email address and finish setting up your account.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleConfirm}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': {
                  bgcolor: '#16a34a',
                },
              }}
            >
              Verify Email Address
            </Button>
          </>
        );

      case 'confirming':
        return (
          <>
            <CircularProgress size={64} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Verifying Your Email
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary }}
            >
              Please wait while we verify your email address...
            </Typography>
          </>
        );

      case 'success':
        return (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Email Verified!
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              {message}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': {
                  bgcolor: '#16a34a',
                },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Verification Failed
            </Typography>
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 0,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                borderLeft: '4px solid #ef4444',
              }}
            >
              {message}
            </Alert>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              The verification link may have expired. You can request a new verification email from the login page.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': {
                  bgcolor: '#16a34a',
                },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      case 'no-token':
        return (
          <>
            <EmailIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Invalid Link
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, mb: 3 }}
            >
              {message}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': {
                  bgcolor: '#16a34a',
                },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.background,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          p: 4,
          textAlign: 'center',
        }}
      >
        {/* Logo / Brand */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#22c55e',
              fontWeight: 700,
              letterSpacing: '-0.025em',
              mb: 1,
            }}
          >
            Foysonis SCM
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary }}
          >
            Email Verification
          </Typography>
        </Box>

        {renderContent()}
      </Box>
    </Box>
  );
};

export default EmailVerificationPage;
